import React, { useState, useEffect } from 'react';
import {
  Alert,
  Chip,
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Paper,
  Stack
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  BarChart as BarChartIcon,
  People as PeopleIcon,
  ErrorOutline as ErrorIcon,
  Share as ShareIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  ShowChart as ShowChartIcon,
  Warning as WarningIcon,
  ViewKanban as KanbanIcon,
  Description as ContractIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import {
  AdminErpWorkspace,
  AdminKanbanBoard,
  MetricCard,
  PlanDistributionCard,
  ReferralStatsCard,
  RevenueForecastCard,
  UsersTable,
  ErrorsTable,
  PaymentsTable,
  ReferralsTable
} from '../components/admin';

import adminService from '../services/AdminService';
import {
  AdminReferral,
  AdminUser,
  AdminDashboardData,
  ErrorLog,
  PaymentRecord
} from '../types/admin/AdminTypes';

interface TabPanelProps {
  readonly children?: React.ReactNode;
  readonly index: number;
  readonly value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const AdminDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<AdminDashboardData | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(20);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsRowsPerPage, setPaymentsRowsPerPage] = useState(20);
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [errorsTotal, setErrorsTotal] = useState(0);
  const [errorsPage, setErrorsPage] = useState(1);
  const [errorsRowsPerPage, setErrorsRowsPerPage] = useState(20);
  const [referrals, setReferrals] = useState<AdminReferral[]>([]);
  const [referralsTotal, setReferralsTotal] = useState(0);
  const [referralsPage, setReferralsPage] = useState(1);
  const [referralsRowsPerPage, setReferralsRowsPerPage] = useState(20);
  
  // Estados para filtros
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userFilterPlan, setUserFilterPlan] = useState('');
  const [userFilterStatus, setUserFilterStatus] = useState('');
  const [referralSearchTerm, setReferralSearchTerm] = useState('');
  
  // Estado para modal de erro
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  
  // Estado para modal de acesso gratuito
  const [freeAccessDialog, setFreeAccessDialog] = useState<{ open: boolean; userId: string | null }>({
    open: false,
    userId: null
  });
  const [freeAccessReason, setFreeAccessReason] = useState('');

  // Estado para modal de reset de senha
  const [resetPasswordDialog, setResetPasswordDialog] = useState<{ open: boolean; userId: string | null }>({
    open: false,
    userId: null
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      const data = await adminService.getDashboardData();
      setDashboardData(data);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const loadUsers = async () => {
    try {
      const response = await adminService.getUsers(usersPage, usersRowsPerPage, {
        search: userSearchTerm || undefined,
        plan: userFilterPlan || undefined,
        status: userFilterStatus || undefined
      });
      setUsers(response.users);
      setUsersTotal(response.total);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    }
  };

  const loadPayments = async () => {
    try {
      const response = await adminService.getPayments(paymentsPage, paymentsRowsPerPage);
      setPayments(response.payments);
      setPaymentsTotal(response.total);
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error);
      toast.error('Erro ao carregar pagamentos');
    }
  };

  const loadErrors = async () => {
    try {
      const response = await adminService.getErrorLogs(errorsPage, errorsRowsPerPage);
      setErrors(response.errors);
      setErrorsTotal(response.total);
    } catch (error) {
      console.error('Erro ao carregar erros:', error);
      toast.error('Erro ao carregar erros');
    }
  };

  const loadReferrals = async () => {
    try {
      const response = await adminService.getReferrals(referralsPage, referralsRowsPerPage, {
        search: referralSearchTerm || undefined
      });
      setReferrals(response.referrals);
      setReferralsTotal(response.total);
    } catch (error) {
      console.error('Erro ao carregar referrals:', error);
      toast.error('Erro ao carregar referrals');
    }
  };

  useEffect(() => {
    loadUsers();
  }, [usersPage, usersRowsPerPage, userSearchTerm, userFilterPlan, userFilterStatus]);

  useEffect(() => {
    loadPayments();
  }, [paymentsPage, paymentsRowsPerPage]);

  useEffect(() => {
    loadErrors();
  }, [errorsPage, errorsRowsPerPage]);

  useEffect(() => {
    loadReferrals();
  }, [referralsPage, referralsRowsPerPage, referralSearchTerm]);

  const handleStatusChange = async (userId: string, status: 'active' | 'inactive' | 'suspended') => {
    try {
      await adminService.updateUserStatus(userId, status);
      toast.success('Status do usuário atualizado');
      await Promise.all([loadDashboardData(), loadUsers()]);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status do usuário');
    }
  };

  const handleGrantFreeAccess = (userId: string) => {
    setFreeAccessDialog({ open: true, userId });
  };

  const confirmGrantFreeAccess = async () => {
    if (!freeAccessDialog.userId) return;
    try {
      await adminService.grantFreeAccess(freeAccessDialog.userId, freeAccessReason);
      toast.success('Acesso gratuito concedido');
      setFreeAccessDialog({ open: false, userId: null });
      setFreeAccessReason('');
      await Promise.all([loadDashboardData(), loadUsers()]);
    } catch (error) {
      console.error('Erro ao conceder acesso:', error);
      toast.error('Erro ao conceder acesso gratuito');
    }
  };

  const handleResolveError = async (errorId: string) => {
    try {
      await adminService.resolveError(errorId);
      toast.success('Erro marcado como resolvido');
      await Promise.all([loadDashboardData(), loadErrors()]);
    } catch (error) {
      console.error('Erro ao resolver:', error);
      toast.error('Erro ao resolver');
    }
  };

  const handleRevokeFreeAccess = async (userId: string) => {
    try {
      await adminService.revokeFreeAccess(userId);
      toast.success('Acesso gratuito revogado');
      await Promise.all([loadDashboardData(), loadUsers()]);
    } catch (error) {
      console.error('Erro ao revogar acesso:', error);
      toast.error('Erro ao revogar acesso gratuito');
    }
  };

  const handleResetPassword = (userId: string) => {
    setResetPasswordDialog({ open: true, userId });
  };

  const confirmResetPassword = async () => {
    if (!resetPasswordDialog.userId) return;
    try {
      await adminService.resetPassword(resetPasswordDialog.userId, newPassword);
      toast.success('Senha resetada com sucesso');
      setResetPasswordDialog({ open: false, userId: null });
      setNewPassword('');
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      toast.error('Erro ao resetar senha');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">Erro ao carregar dados do dashboard</Typography>
      </Box>
    );
  }

  const { metrics, planDistribution, referralStats, revenueForecast, financialOverview, taskBoard } = dashboardData;
  const refreshDashboardSnapshot = async () => {
    await loadDashboardData({ silent: true });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Painel operacional do SaaS, ERP interno e gestão administrativa
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48
            }
          }}
        >
          <Tab icon={<BarChartIcon />} iconPosition="start" label="Visão Geral" />
          <Tab icon={<BankIcon />} iconPosition="start" label="ERP" />
          <Tab icon={<KanbanIcon />} iconPosition="start" label="Kanban" />
          <Tab icon={<PeopleIcon />} iconPosition="start" label="Usuários" />
          <Tab icon={<ErrorIcon />} iconPosition="start" label="Erros" />
          <Tab icon={<ShareIcon />} iconPosition="start" label="Referrals" />
          <Tab icon={<MoneyIcon />} iconPosition="start" label="Pagamentos" />
        </Tabs>
      </Box>

      {/* Tab: Visão Geral */}
      <TabPanel value={tabValue} index={0}>
        {/* Métricas Principais */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Total de Usuários"
              value={metrics.totalUsers}
              subtitle={`${metrics.newUsersThisMonth} novos este mês`}
              icon={<PeopleIcon sx={{ color: '#1976d2' }} />}
              iconBgColor="#e3f2fd"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Usuários Ativos"
              value={metrics.activeUsers}
              subtitle={`${((metrics.activeUsers / metrics.totalUsers) * 100).toFixed(1)}% do total`}
              icon={<CheckCircleIcon sx={{ color: '#4caf50' }} />}
              iconBgColor="#e8f5e9"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="MRR"
              value={formatCurrency(metrics.mrr)}
              subtitle={`ARR: ${formatCurrency(metrics.arr)}`}
              icon={<MoneyIcon sx={{ color: '#ff9800' }} />}
              iconBgColor="#fff3e0"
              trend={{ value: metrics.mrrGrowth, isPositive: metrics.mrrGrowth >= 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Churn Rate"
              value={`${metrics.churnRate.toFixed(1)}%`}
              subtitle={`${metrics.churnCount} cancelamentos`}
              icon={<WarningIcon sx={{ color: '#4caf50' }} />}
              iconBgColor="#e8f5e9"
            />
          </Grid>
        </Grid>

        {/* Métricas Secundárias */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="ARPU"
              value={formatCurrency(metrics.arpu)}
              subtitle="Receita média por usuário"
              icon={<BarChartIcon sx={{ color: '#9c27b0' }} />}
              iconBgColor="#f3e5f5"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Retenção"
              value={`${metrics.retentionRate.toFixed(1)}%`}
              subtitle="Taxa de retenção"
              icon={<TrendingUpIcon sx={{ color: '#00bcd4' }} />}
              iconBgColor="#e0f7fa"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="LTV Estimado"
              value={formatCurrency(metrics.ltv)}
              subtitle="Lifetime Value"
              icon={<ShowChartIcon sx={{ color: '#1976d2' }} />}
              iconBgColor="#e3f2fd"
            />
          </Grid>
        </Grid>

        {/* Distribuição e Referrals */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <PlanDistributionCard
              plans={planDistribution}
              onPlanClick={(plan) => {
                setUserFilterPlan(plan.toLowerCase().includes('free') ? 'free' : 'pro');
                setTabValue(3);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <ReferralStatsCard stats={referralStats} />
          </Grid>
        </Grid>

        {/* Previsão de Receita */}
        <RevenueForecastCard forecast={revenueForecast} />

        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
            ERP e Operação
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Resumo financeiro híbrido, contratos e execução das tarefas internas.
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Caixa ERP"
              value={formatCurrency(financialOverview?.cashBalance ?? 0)}
              subtitle="Saldo consolidado manual"
              icon={<BankIcon sx={{ color: '#2e7d32' }} />}
              iconBgColor="#e8f5e9"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Receita Híbrida"
              value={formatCurrency(financialOverview?.totalRevenuePeriod ?? 0)}
              subtitle={`Site ${formatCurrency(financialOverview?.siteRevenuePeriod ?? 0)} + manual ${formatCurrency(financialOverview?.manualRevenuePeriod ?? 0)}`}
              icon={<MoneyIcon sx={{ color: '#0f6bff' }} />}
              iconBgColor="#eef4ff"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Contratos Ativos"
              value={financialOverview?.activeContracts ?? 0}
              subtitle={`${financialOverview?.contractsExpiringSoon ?? 0} a vencer`}
              icon={<ContractIcon sx={{ color: '#8a5b00' }} />}
              iconBgColor="#fff8e8"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MetricCard
              title="Kanban em Aberto"
              value={taskBoard?.openTasks ?? 0}
              subtitle={`${taskBoard?.completedTasks ?? 0} concluídas`}
              icon={<KanbanIcon sx={{ color: '#7b1fa2' }} />}
              iconBgColor="#f3e5f5"
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e0e0e0',
                height: '100%'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
                Alertas de Contrato
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Vencimentos e pendências que exigem acompanhamento.
              </Typography>

              <Stack spacing={1.5}>
                {financialOverview?.contractAlerts?.length ? (
                  financialOverview.contractAlerts.slice(0, 4).map((alert) => (
                    <Alert key={alert.contractId} severity={alert.alertType === 'vencido' ? 'error' : 'warning'}>
                      <Typography sx={{ fontWeight: 600 }}>{alert.title}</Typography>
                      <Typography variant="body2">
                        {alert.ownerName} • {alert.companyName} • até {new Date(`${alert.endDate}T00:00:00`).toLocaleDateString('pt-BR')}
                      </Typography>
                      <Typography variant="body2">{alert.alertMessage}</Typography>
                    </Alert>
                  ))
                ) : (
                  <Alert severity="success">Nenhum contrato vencido ou a vencer no momento.</Alert>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e0e0e0',
                height: '100%'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
                Snapshot do Kanban
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Distribuição atual das implementações, bugs e pendências gerais.
              </Typography>

              <Stack spacing={1.5}>
                {taskBoard?.columns?.length ? (
                  taskBoard.columns.map((column) => (
                    <Box
                      key={column.id}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{column.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {column.tasks.filter((task) => task.status === 'concluida').length} concluída(s)
                        </Typography>
                      </Box>
                      <Chip label={`${column.total} tarefa(s)`} color="primary" variant="outlined" />
                    </Box>
                  ))
                ) : (
                  <Alert severity="info">Nenhuma tarefa cadastrada no kanban administrativo.</Alert>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <AdminErpWorkspace
          initialFinancialOverview={dashboardData.financialOverview ?? null}
          onDataChanged={refreshDashboardSnapshot}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <AdminKanbanBoard
          initialBoard={dashboardData.taskBoard ?? null}
          onBoardChanged={refreshDashboardSnapshot}
        />
      </TabPanel>

      {/* Tab: Usuários */}
      <TabPanel value={tabValue} index={3}>
        <UsersTable
          users={users}
          onStatusChange={handleStatusChange}
          onGrantFreeAccess={handleGrantFreeAccess}
          onRevokeFreeAccess={handleRevokeFreeAccess}
          onResetPassword={handleResetPassword}
          searchTerm={userSearchTerm}
          onSearchChange={(value) => {
            setUsersPage(1);
            setUserSearchTerm(value);
          }}
          filterPlan={userFilterPlan}
          onFilterPlanChange={(value) => {
            setUsersPage(1);
            setUserFilterPlan(value);
          }}
          filterStatus={userFilterStatus}
          onFilterStatusChange={(value) => {
            setUsersPage(1);
            setUserFilterStatus(value);
          }}
          page={usersPage}
          rowsPerPage={usersRowsPerPage}
          total={usersTotal}
          onPageChange={setUsersPage}
          onRowsPerPageChange={(value) => {
            setUsersRowsPerPage(value);
            setUsersPage(1);
          }}
        />
      </TabPanel>

      {/* Tab: Erros */}
      <TabPanel value={tabValue} index={4}>
        <ErrorsTable
          errors={errors}
          onResolve={handleResolveError}
          onViewDetails={setSelectedError}
          page={errorsPage}
          rowsPerPage={errorsRowsPerPage}
          total={errorsTotal}
          onPageChange={setErrorsPage}
          onRowsPerPageChange={(value) => {
            setErrorsRowsPerPage(value);
            setErrorsPage(1);
          }}
        />
      </TabPanel>

      {/* Tab: Referrals */}
      <TabPanel value={tabValue} index={5}>
        <ReferralsTable
          referrals={referrals}
          searchTerm={referralSearchTerm}
          onSearchChange={(value) => {
            setReferralsPage(1);
            setReferralSearchTerm(value);
          }}
          page={referralsPage}
          rowsPerPage={referralsRowsPerPage}
          total={referralsTotal}
          onPageChange={setReferralsPage}
          onRowsPerPageChange={(value) => {
            setReferralsRowsPerPage(value);
            setReferralsPage(1);
          }}
        />
      </TabPanel>

      {/* Tab: Pagamentos */}
      <TabPanel value={tabValue} index={6}>
        <PaymentsTable
          payments={payments}
          page={paymentsPage}
          rowsPerPage={paymentsRowsPerPage}
          total={paymentsTotal}
          onPageChange={setPaymentsPage}
          onRowsPerPageChange={(value) => {
            setPaymentsRowsPerPage(value);
            setPaymentsPage(1);
          }}
        />
      </TabPanel>

      {/* Modal de Detalhes do Erro */}
      <Dialog open={!!selectedError} onClose={() => setSelectedError(null)} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes do Erro</DialogTitle>
        <DialogContent>
          {selectedError && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Tipo</Typography>
              <Typography sx={{ mb: 2 }}>{selectedError.errorType}</Typography>
              
              <Typography variant="subtitle2" color="text.secondary">Mensagem</Typography>
              <Typography sx={{ mb: 2 }}>{selectedError.message}</Typography>
              
              {selectedError.stackTrace && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">Stack Trace</Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.75rem'
                    }}
                  >
                    {selectedError.stackTrace}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedError(null)}>Fechar</Button>
          {selectedError && !selectedError.resolved && (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                handleResolveError(selectedError.id);
                setSelectedError(null);
              }}
            >
              Marcar como Resolvido
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal de Acesso Gratuito */}
      <Dialog 
        open={freeAccessDialog.open} 
        onClose={() => setFreeAccessDialog({ open: false, userId: null })}
      >
        <DialogTitle>Conceder Acesso Gratuito</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Informe o motivo para conceder acesso gratuito a este usuário:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={freeAccessReason}
            onChange={(e) => setFreeAccessReason(e.target.value)}
            placeholder="Ex: Parceiro estratégico, beta tester, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFreeAccessDialog({ open: false, userId: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmGrantFreeAccess}
            disabled={!freeAccessReason.trim()}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Reset de Senha */}
      <Dialog
        open={resetPasswordDialog.open}
        onClose={() => setResetPasswordDialog({ open: false, userId: null })}
      >
        <DialogTitle>Resetar Senha do Usuário</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Informe a nova senha para este usuário (mínimo 8 caracteres):
          </Typography>
          <TextField
            fullWidth
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Nova senha"
            inputProps={{ minLength: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setResetPasswordDialog({ open: false, userId: null });
            setNewPassword('');
          }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmResetPassword}
            disabled={newPassword.length < 8}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
